// 内容审核 — 人工复审队列 (content-moderation-gate).
//
// 机审 (阿里云绿网) 三态: high→block / medium→review / 其余→pass. verdict=review
// 的记录进入 pending_review 待人工处置; 本页让运营看图 + 放行(approve)或删除(remove).
// 默认只看待处置队列; 可切「全量台账」审计机审历史 (含 pass/block/error).
//
// 后端: handlers/moderation/admin.go (GET /records, POST /records/:id/handle).

import { useState } from 'react';
import { Button, Image, Popconfirm, Space, Tag, Tooltip, Typography, message } from 'antd';
import { CheckOutlined, DeleteOutlined } from '@ant-design/icons';
import { ResourceList } from '@/components/ResourceList';
import { useAuthStore } from '@/stores/auth';
import {
  listModerationRecords,
  handleModerationRecord,
  type ModerationRecord,
  type ModerationAction,
  type ModerationVerdict,
  type ModerationScene,
  type ModerationStatus,
} from '@/api/moderation';
import { formatDateTime, truncate } from '@/utils/format';

const SCENE_META: Record<ModerationScene, { label: string; color: string }> = {
  face_upload: { label: '上传人脸', color: 'geekblue' },
  result: { label: '生成结果', color: 'purple' },
};

const VERDICT_META: Record<ModerationVerdict, { label: string; color: string }> = {
  pass: { label: '通过', color: 'green' },
  review: { label: '待复审', color: 'gold' },
  block: { label: '拦截', color: 'red' },
  error: { label: '机审失败', color: 'default' },
};

const STATUS_META: Record<ModerationStatus, { label: string; color: string }> = {
  pending_review: { label: '待处置', color: 'gold' },
  approved: { label: '已放行', color: 'green' },
  removed: { label: '已删除', color: 'red' },
  n_a: { label: '无需处置', color: 'default' },
};

export function ModerationQueuePage() {
  // ResourceList 不暴露 reload 给列 render — 用 reloadKey 强制 remount 重新拉取
  // (与 fulfillments/skus/cardkeys 页同一惯用法).
  const [reloadKey, setReloadKey] = useState(0);
  const profile = useAuthStore((s) => s.profile);
  const handledBy = profile?.username || profile?.display_name || undefined;

  const handle = async (id: number, action: ModerationAction) => {
    try {
      await handleModerationRecord(id, action, handledBy);
      message.success(action === 'approve' ? '已放行' : '已删除');
      setReloadKey((k) => k + 1);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '处置失败');
    }
  };

  return (
    <ResourceList<ModerationRecord>
      key={reloadKey}
      title="内容审核"
      description="机审 (阿里云绿网) 判为「待复审」的内容在此人工处置: 放行 (内容其实没问题) 或删除 (确认违规, 同时删 OSS 对象). 默认只看待处置队列; 切「全量台账」可审计机审历史. 算法备案要求的人工复审入口."
      rowKey="id"
      pagination="cursor"
      defaultPageSize={20}
      filters={[
        {
          key: 'status',
          label: '范围',
          type: 'select',
          options: [
            { value: 'pending_review', label: '待复审队列' },
            { value: '', label: '全量台账' },
            { value: 'approved', label: '已放行' },
            { value: 'removed', label: '已删除' },
          ],
        },
      ]}
      fetcher={({ filters, pageSize, cursor }) =>
        listModerationRecords({
          // 默认待复审队列 (filters.status 未选时)
          status: filters.status === undefined ? 'pending_review' : (filters.status as string),
          cursor,
          limit: pageSize,
        })
      }
      columns={[
        {
          title: '内容',
          dataIndex: 'image_url',
          width: 80,
          render: (v: string) =>
            v ? (
              <Image src={v} width={56} height={56} style={{ objectFit: 'cover', borderRadius: 4 }} />
            ) : (
              <Typography.Text type="secondary">-</Typography.Text>
            ),
        },
        {
          title: '场景',
          dataIndex: 'scene',
          render: (v: ModerationScene) => <Tag color={SCENE_META[v]?.color}>{SCENE_META[v]?.label ?? v}</Tag>,
        },
        {
          title: '机审结论',
          dataIndex: 'verdict',
          render: (v: ModerationVerdict) => <Tag color={VERDICT_META[v]?.color}>{VERDICT_META[v]?.label ?? v}</Tag>,
        },
        {
          title: '风险标签',
          dataIndex: 'risk_labels',
          render: (labels: ModerationRecord['risk_labels']) => {
            if (!labels || labels.length === 0) return <Typography.Text type="secondary">-</Typography.Text>;
            return (
              <Space size={4} wrap>
                {labels.map((l, i) => (
                  <Tooltip key={i} title={`置信度 ${(l.confidence * 100).toFixed(0)}%`}>
                    <Tag color="volcano">{l.label}</Tag>
                  </Tooltip>
                ))}
              </Space>
            );
          },
        },
        {
          title: '用户 / 任务',
          key: 'ref',
          render: (_, row) => (
            <Space direction="vertical" size={0}>
              {row.user_id ? (
                <Typography.Text style={{ fontSize: 12 }}>u: {truncate(row.user_id, 14)}</Typography.Text>
              ) : null}
              {row.task_id ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  t: {truncate(row.task_id, 14)}
                </Typography.Text>
              ) : null}
              {!row.user_id && !row.task_id ? <Typography.Text type="secondary">-</Typography.Text> : null}
            </Space>
          ),
        },
        {
          title: '状态',
          dataIndex: 'status',
          render: (v: ModerationStatus) => <Tag color={STATUS_META[v]?.color}>{STATUS_META[v]?.label ?? v}</Tag>,
        },
        {
          title: '处置人 / 时间',
          key: 'handled',
          render: (_, row) =>
            row.handled_by ? (
              <Space direction="vertical" size={0}>
                <Typography.Text style={{ fontSize: 12 }}>{row.handled_by}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {formatDateTime(row.handled_at)}
                </Typography.Text>
              </Space>
            ) : (
              <Typography.Text type="secondary">-</Typography.Text>
            ),
        },
        {
          title: '送审时间',
          dataIndex: 'created_at',
          render: (v: string) => formatDateTime(v),
        },
        {
          title: '操作',
          key: 'actions',
          width: 150,
          fixed: 'right',
          render: (_, row) => {
            if (row.status !== 'pending_review') return null;
            return (
              <Space>
                <Popconfirm
                  title="放行该内容?"
                  description="机审误判 / 内容其实合规, 保留."
                  onConfirm={() => handle(row.id, 'approve')}
                  okText="放行"
                  cancelText="取消"
                >
                  <Button type="link" size="small" icon={<CheckOutlined />} style={{ padding: 0 }}>
                    放行
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title="删除该内容?"
                  description="确认违规 / 不当, 将同时删除 OSS 对象, 不可恢复."
                  onConfirm={() => handle(row.id, 'remove')}
                  okText="删除"
                  okButtonProps={{ danger: true }}
                  cancelText="取消"
                >
                  <Button type="link" size="small" danger icon={<DeleteOutlined />} style={{ padding: 0 }}>
                    删除
                  </Button>
                </Popconfirm>
              </Space>
            );
          },
        },
      ]}
    />
  );
}
